import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsAlphaValidator,
  numbersOnlyValidator,
  numericValidator,
} from '../../core/helpers/validator';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Storage } from '@angular/fire/storage';
import { BadgesService } from '../../features/badges/badges.service';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import moment from 'moment';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-badges-new',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    FullscreenLoaderComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    ContentLoaderComponent,
  ],
  templateUrl: './badges-new.component.html',
  styleUrl: './badges-new.component.scss',
})
export class BadgesNewComponent {
  @ViewChild('imagefileInput') imagefileInput!: ElementRef<HTMLInputElement>;

  readonly storage = inject(Storage);
  readonly dialog = inject(MatDialog);

  public languages: any[] = [];
  public mainForm: FormGroup;
  public langIdArray: { id?: number; langId: number }[] = [];
  public badgeTranslations: { id?: number; langId: number; title: string }[] =
    [];

  public currentBadge: any = null;

  onEdit: boolean = false;
  loading: boolean = false;
  isSubmitting: boolean = false;
  imageUploading: boolean = false;

  showWords: boolean = false;
  showCount: boolean = false;
  showDays: boolean = false;
  showMinutes: boolean = false;

  imageUrl: string | null = null;
  currentTitle: string | null = null;
  updatedTitle: string | null = null;
  existingTitle: string | null = null;
  imageFileName: string | null = null;
  badgeId: any;
  streakDays: number | null = null;
  currId: number | undefined;
  contentLoading: boolean = false;
  badgeTypeOptions = [
    { value: 'read', label: 'Read' },
    { value: 'event', label: 'Event' },
  ];
  subTypes = [
    { value: 'time', label: 'Time Spent' },
    { value: 'word', label: 'Words Read' },
    { value: 'count', label: 'Book Count' },
    { value: 'streak', label: 'Streak' },
  ];

  private disableRestrictedFields() {
    this.mainForm.get('badgeType')?.disable();
    this.mainForm.get('type')?.disable();
    this.mainForm.get('point')?.disable();
    this.mainForm.get('minutes')?.disable();
    this.mainForm.get('inDays')?.disable();
    this.mainForm.get('words')?.disable();
    this.mainForm.get('count')?.disable();
    this.mainForm.get('from')?.disable();
    this.mainForm.get('to')?.disable();
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private badgesService: BadgesService
  ) {
    this.mainForm = this.fb.group({
      badgeType: ['', [Validators.required]],
      imageURL: [''],
      title: [
        '',
        [
          Validators.required,
          Validators.maxLength(150),
          noOnlySpecialCharsAlphaValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [1, Validators.required],
      type: [null, [Validators.required]],
      point: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          numericValidator(1, 5000),
          numbersOnlyValidator(),
        ],
      ],
      minutes: [null],
      inDays: [null],
      words: [null],
      count: [null],
      from: [null],
      to: [null],
    });
  }

  watchTypeChanges() {
    this.mainForm.get('type')?.valueChanges.subscribe((type) => {
      this.resetConditionalFields();
      switch (type) {
        case 'time':
          this.showMinutes = true;
          this.mainForm
            .get('minutes')
            ?.setValidators([
              Validators.required,
              numericValidator(1, 100000),
              numbersOnlyValidator(),
            ]);
          break;
        case 'word':
          this.showWords = true;
          this.mainForm
            .get('words')
            ?.setValidators([
              Validators.required,
              numericValidator(50, 500000),
              numbersOnlyValidator(),
            ]);
          break;
        case 'count':
          this.showCount = true;
          this.mainForm
            .get('count')
            ?.setValidators([
              Validators.required,
              numericValidator(1, 999),
              numbersOnlyValidator(),
            ]);
          break;
        case 'streak':
          this.showDays = true;
          this.showWords = true;
          this.mainForm
            .get('words')
            ?.setValidators([
              Validators.required,
              numericValidator(50, 500000),
              numbersOnlyValidator(),
            ]);
          this.mainForm
            .get('inDays')
            ?.setValidators([
              Validators.required,
              numericValidator(1, 999),
              numbersOnlyValidator(),
            ]);
          if (this.mainForm.get('badgeType')?.value === 'event') {
            this.mainForm.get('inDays')?.patchValue(this.streakDays || null);
          }
          break;
      }

      this.mainForm.get('minutes')?.updateValueAndValidity();
      this.mainForm.get('inDays')?.updateValueAndValidity();
      this.mainForm.get('words')?.updateValueAndValidity();
      this.mainForm.get('count')?.updateValueAndValidity();
    });
  }

  resetConditionalFields() {
    this.showMinutes = false;
    this.showWords = false;
    this.showCount = false;
    this.showDays = false;

    this.mainForm.get('minutes')?.clearValidators();
    this.mainForm.get('minutes')?.reset();

    this.mainForm.get('inDays')?.clearValidators();
    this.mainForm.get('inDays')?.reset();

    this.mainForm.get('words')?.clearValidators();
    this.mainForm.get('words')?.reset();

    this.mainForm.get('count')?.clearValidators();
    this.mainForm.get('count')?.reset();
  }

  ngOnInit() {
    this.getLanguages();
    this.mainForm.get('badgeType')?.valueChanges.subscribe((badgeType) => {
      if (badgeType === 'event') {
        this.mainForm.get('from')?.setValidators([Validators.required]);
        this.mainForm.get('to')?.setValidators([Validators.required]);
      } else {
        this.mainForm.get('from')?.clearValidators();
        this.mainForm.get('to')?.clearValidators();
      }
      this.mainForm.get('from')?.reset();
      this.mainForm.get('to')?.reset();
    });

    this.mainForm
      .get('from')
      ?.valueChanges.subscribe(() => this.updateStreakDays());
    this.mainForm
      .get('to')
      ?.valueChanges.subscribe(() => this.updateStreakDays());

    this.watchTypeChanges();

    this.route.paramMap.subscribe((params) => {
      this.badgeId = params.get('id');
      if (this.badgeId) {
        this.contentLoading = true;
        this.getBadgeById(this.badgeId);
        this.mainForm.get('langId')?.patchValue(null);
      }
    });

    if (this.badgeId) {
      this.disableRestrictedFields();
    }
  }

  disableBeforeFrom = (date: Date | null): boolean => {
    const from = this.mainForm.get('from')?.value;
    if (!from || !date) {
      return true;
    }
    return date >= new Date(from);
  };

  disableAfterTo = (date: Date | null): boolean => {
    const to = this.mainForm.get('to')?.value;
    if (!to || !date) {
      return true;
    }
    return date <= new Date(to);
  };

  disableBeforeToday = (date: Date | null): boolean => {
    const today = new Date();
    if (!date) {
      return true;
    }
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date >= today;
  };

  combinedFilterFrom = (date: Date | null): boolean => {
    return this.disableAfterTo(date) && this.disableBeforeToday(date);
  };

  combinedFilterTo = (date: Date | null): boolean => {
    return this.disableBeforeFrom(date) && this.disableBeforeToday(date);
  };

  updateStreakDays() {
    const badgeType = this.mainForm.get('badgeType')?.value;
    const fromValue = this.mainForm.get('from')?.value;
    const toValue = this.mainForm.get('to')?.value;

    if (badgeType === 'event' && fromValue && toValue) {
      const from = moment(fromValue);
      const to = moment(toValue);

      if (from.isValid() && to.isValid()) {
        this.streakDays = to.diff(from, 'days') + 1;
        this.mainForm.get('inDays')?.patchValue(this.streakDays);
        this.mainForm.get('inDays')?.updateValueAndValidity();
        if (this.badgeId) {
          this.mainForm.get('inDays')?.disable();
        }
      }
    }
  }


  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getBadgeById(id: any) {
    this.badgesService.getBadgeById(id).subscribe({
      next: (res) => {
        const badgeData = res;
        this.currentBadge = badgeData;
        if (badgeData) {
          this.mainForm.patchValue({
            badgeType: badgeData.badgeType,
            type: badgeData.type,
            point: badgeData.point,
            from: badgeData.period?.from,
            to: badgeData.period?.to,
          });

          this.imageUrl = badgeData.imgUrl;

          this.imageFileName = badgeData.imgUrl
            ? FilenameGeneratorUtil.generateRandomFilename(badgeData.imgUrl)
            : null;

          badgeData.badgeTranslations.forEach((translation: any) => {
            this.badgeTranslations.push({
              id: translation.id,
              langId: translation.langId,
              title: translation.title,
            });
          });
          badgeData.badgeTranslations.forEach((translation: any) => {
            this.langIdArray.push({
              id: translation.id,
              langId: translation.langId,
            });
          });
          if (badgeData.badgeType === 'event') {
            // Set required validators for event dates
            this.mainForm.get('from')?.setValidators([Validators.required]);
            this.mainForm.get('to')?.setValidators([Validators.required]);
            this.mainForm.get('from')?.updateValueAndValidity();
            this.mainForm.get('to')?.updateValueAndValidity();

            // Calculate streakDays
            this.updateStreakDays();
          }

          const condition = badgeData.condition || {};
          this.mainForm.patchValue({
            count: badgeData.condition?.count ?? null,
            words: badgeData.condition?.words ?? null,
            minutes: badgeData.condition?.time ?? null,
            inDays: badgeData.condition?.inDays ?? null,
          });
          if (this.badgeTranslations.length === this.languages.length) {
            this.mainForm.get('title')?.disable();
            this.mainForm.get('langId')?.disable();
          }
        }
        this.contentLoading = false;
      },
      error: (err) => {
        this.contentLoading = false;
      },
    });
  }

  onCancel() {
    this.router.navigate(['/badge']);
  }

  blockNegativeNumbers(event: KeyboardEvent): boolean {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    const isNumber = /[0-9]/.test(event.key);
    const isDecimal = event.key === '.';
    const isPasteShortcut =
      (event.ctrlKey || event.metaKey) && event.key === 'v';
    return isNumber || isDecimal || isPasteShortcut;
  }

  onPaste(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
      this.toastr.warning('Only numbers are allowed');
    }
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedTranslations() {
    return this.badgeTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  isLangUsed(langId: number): boolean {
    return this.badgeTranslations?.some((t) => t.langId === langId);
  }

  addBadgeTable(): void {
    const { title, langId } = this.mainForm.value;

    if (title === '' || title === null) {
      this.toastr.warning('Please enter valid badge name.');
      return;
    }

    if (langId === '' || langId === null) {
      this.toastr.warning('Please choose a valid language');
      return;
    }

    this.badgeTranslations.push({
      ...(this.onEdit ? { id: this.currId } : {}),
      title,
      langId,
    });

    this.onEdit = false;
    if (this.badgeTranslations.length === this.languages.length) {
      this.mainForm.get('title')?.disable();
      this.mainForm.get('langId')?.disable();
    }

    this.mainForm.controls?.['title'].reset();
    this.mainForm.controls?.['title'].setErrors(null);
    this.mainForm.controls?.['title'].markAsUntouched();

    this.mainForm.controls?.['langId'].reset();
    this.mainForm.controls?.['langId'].setErrors(null);
    this.mainForm.controls?.['langId'].markAsUntouched();
  }

  onDeleteBadgeTranslation(lang: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        let id;
        if (this.badgeId) {
          const existingTranslation = this.langIdArray.find(
            (t) => t.langId === lang
          );
          id = existingTranslation?.id;
          if (id) {
            this.badgesService.deleteBadgeTranslation(id).subscribe({
              next: () => {
                this.badgeTranslations.splice(index, 1);
                this.langIdArray.splice(index, 1)
              },
              error: (err) => {
                console.error(err);
              },
            });
          }
        } else {
          this.badgeTranslations.splice(index, 1);
        }
      }
    });
  }


  editBadgeTranslation(index: number): void {
    this.onEdit = true;
    this.mainForm.get('title')?.enable();
    this.mainForm.get('langId')?.enable();

    const selected = this.badgeTranslations[index];
    this.currId = selected?.id;
    this.existingTitle = this.mainForm.get('title')?.value;
    this.currentTitle = selected.title;

    if (this.existingTitle && this.existingTitle.trim() !== '') {
      this.toastr.warning('Please add the current entry first.');
      this.scrollUp(`translation`);
      return;
    }

    this.badgeTranslations.splice(index, 1);
    this.mainForm.patchValue({
      title: selected.title,
      langId: selected.langId,
    });
    this.mainForm.get('title')?.markAsTouched();
    this.mainForm.get('langId')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  async onImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (less than 15 MB)');
      input.value = '';
      return;
    }

    this.imageUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.imageUploading = false;
      return;
    }

    const filePath = `badges/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.imageUploading = false;
        return;
      }
      this.imageUrl = await getDownloadURL(fileRef);
      this.imageFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
      console.error('Upload failed--', err);
    } finally {
      this.imageUploading = false;
    }
  }

  clearImageUpload(): void {
    this.imageFileName = null;
    this.imageUrl = null;
    if (this.imagefileInput) {
      this.imagefileInput.nativeElement.value = '';
    }
  }

  buildConditionString(): string {
    const type = this.mainForm.value.type;
    switch (type) {
      case 'time':
        return `{"metric":"TimeSpend","minutes":${this.mainForm.value.minutes}`;
      case 'word':
        return `{"metric":"WordsRead","words":${this.mainForm.value.words}}`;
      case 'streak':
        return `{"metric":"Streak","count":${(this.mainForm.value.inDays, this.mainForm.value.words)
          }}`;
      case 'count':
        return `{"metric":"CompletedStories","count":${this.mainForm.value.count}}`;
      default:
        return '';
    }
  }

  buildConditionObject(): any {
    const type = this.mainForm.value.type;
    const condition: any = {};

    // If we're editing an existing badge, preserve the existing condition structure
    if (this.badgeId && this.mainForm.value.type) {
      // Get current values from form or use existing values as fallback
      switch (type) {
        case 'time':
          condition.metric = 'TimeSpend';
          condition.time = Number(this.mainForm.value.minutes) || this.mainForm.get('minutes')?.value || 0;
          break;
        case 'word':
          condition.metric = 'WordsRead';
          condition.words = Number(this.mainForm.value.words) || this.mainForm.get('words')?.value || 0;
          break;
        case 'count':
          condition.metric = 'CompletedStories';
          condition.count = Number(this.mainForm.value.count) || this.mainForm.get('count')?.value || 0;
          break;
        case 'streak':
          condition.metric = 'Streak';
          condition.words = Number(this.mainForm.value.words) || this.mainForm.get('words')?.value || 0;
          condition.inDays = Number(this.mainForm.value.inDays) || this.mainForm.get('inDays')?.value || 0;
          break;
      }
    } else {
      // For new badges or when type is not set
      switch (type) {
        case 'time':
          if (this.mainForm.value.minutes) {
            condition.metric = 'TimeSpend';
            condition.time = Number(this.mainForm.value.minutes);
          }
          break;
        case 'word':
          if (this.mainForm.value.words) {
            condition.metric = 'WordsRead';
            condition.words = Number(this.mainForm.value.words);
          }
          break;
        case 'count':
          if (this.mainForm.value.count) {
            condition.metric = 'CompletedStories';
            condition.count = Number(this.mainForm.value.count);
          }
          break;
        case 'streak':
          if (this.mainForm.value.words && this.mainForm.value.inDays) {
            condition.metric = 'Streak';
            condition.words = Number(this.mainForm.value.words);
            condition.inDays = Number(this.mainForm.value.inDays);
          }
          break;
      }
    }

    return condition;
  }

createBadge() {
  this.mainForm.get('title')?.enable();
  this.mainForm.get('langId')?.enable();

  // (meaning lang/title fields are intentionally disabled)
  const translationsFull = this.badgeTranslations.length === this.languages.length;
  if (translationsFull) {
    this.mainForm.controls['langId'].setErrors(null);
    this.mainForm.controls['title'].setErrors(null);
  }

  this.mainForm.markAllAsTouched();

  const pendingTitle = this.mainForm.value.title;
  const pendingLangId = this.mainForm.value.langId;

  if (pendingTitle && pendingLangId) {
    // Validate title before adding
    const titleControl = this.mainForm.get('title');
    if (titleControl?.invalid) {
      this.toastr.warning('Please fill all required fields correctly');
      return;
    }
    this.addBadgeTable();
  }

  this.mainForm.controls['langId'].setErrors(null);
  this.mainForm.controls['title'].setErrors(null);

  const formWithoutTranslation = ['badgeType', 'type', 'point', 'from', 'to', 'minutes', 'inDays', 'words', 'count'];
  const hasInvalid = formWithoutTranslation.some(field => {
    const control = this.mainForm.get(field);
    return control && control.enabled && control.invalid;
  });

  if (hasInvalid) {
    this.toastr.warning('Please fill all required fields correctly');
    return;
  }

  if (!this.badgeTranslations.some((t) => t.langId === 1)) {
    this.toastr.error('Badge in English is mandatory, Please add it to continue');
    return;
  }

  if (!this.badgeTranslations.length) {
    this.toastr.warning('Add at least one translation to save');
    return;
  }

  if (!this.imageUrl) {
    this.toastr.error('Please upload a badge image before saving');
    return;
  }

  if (this.mainForm.get('badgeType')?.value === 'event') {
    this.mainForm.get('inDays')?.enable();
  }

  const payload = {
    ...(this.badgeId && { id: Number(this.badgeId) }),
    status: 1,
    badgeType: this.mainForm.getRawValue().badgeType,  // getRawValue() reads disabled fields too
    point: this.mainForm.getRawValue().point,
    ...(this.mainForm.getRawValue().badgeType === 'event' && {
      period: {
        from: moment(this.mainForm.getRawValue().from).format('YYYY-MM-DD'),
        to: moment(this.mainForm.getRawValue().to).format('YYYY-MM-DD'),
      },
    }),
    imgUrl: this.imageUrl,
    type: this.mainForm.getRawValue().type,
    condition:
      Object.keys(this.buildConditionObject()).length > 0
        ? this.buildConditionObject()
        : this.currentBadge?.condition || {},
    badgeTranslations: this.badgeTranslations,
  };

  this.loading = true;
  this.isSubmitting = true;

  if (this.badgeId) {
    this.badgesService.updateBadge(payload).subscribe({
      next: () => {
        this.toastr.success('Badge updated successfully.');
        this.mainForm.reset();
        this.isSubmitting = false;
        this.router.navigate(['/badge']);
      },
      error: () => {
        this.isSubmitting = false;
        this.loading = false;
      },
      complete: () => {
        this.isSubmitting = false;
        this.loading = false;
      },
    });
  } else {
    this.badgesService.createBadge(payload).subscribe({
      next: () => {
        this.toastr.success('Badge created successfully.');
        this.mainForm.reset();
        this.isSubmitting = false;
        this.router.navigate(['/badge']);
      },
      error: () => {
        this.loading = false;
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
        this.loading = false;
      },
    });
  }
}
}
